'use client';

import classNames from 'classnames';
import { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import MapOverlay from '@alltrails/amplitude/enums/MapOverlay';
import logExploreViewSwitched from '@alltrails/amplitude/events/logExploreViewSwitched';
import Button from '@alltrails/denali/components/Button';
import List from '@alltrails/denali/icons/List';
import MapFilled from '@alltrails/denali/icons/MapFilled';
import useMap from '@alltrails/maps/hooks/useMap';
import { getMapBaseLayer, getMapOverlay } from '@alltrails/maps/utils/amplitudeHelpers';
import useIsPage from '@/hooks/useIsPage';
import { getFilterNames } from '@/utils/analyticsHelpers';
import { useAppDispatch, useAppSelector } from '@/utils/redux';
import { setIsShowingMobileList } from '@/utils/slices/exploreSlice';
import styles from './MobileViewToggle.module.scss';

const MobileViewToggle = ({ className }: { className?: string }) => {
  const map = useMap();
  const { isExploreTrail } = useIsPage();
  const dispatch = useAppDispatch();
  const {
    baseStyleId,
    distanceAwayIsochrone,
    dynamicStyleIds,
    exploreServiceFilters,
    exploreViewType,
    isShowingMobileList,
    renderedPOIs,
    searchResultsSummary,
    staticStyleIds,
    trailEngagement
  } = useAppSelector(state => ({
    baseStyleId: state.map.baseStyleId,
    distanceAwayIsochrone: state.exploreService.distanceAwayIsochrone,
    dynamicStyleIds: state.map.dynamicStyleIds,
    exploreServiceFilters: state.exploreService.filters,
    exploreViewType: state.explore.exploreViewType,
    isShowingMobileList: state.explore.isShowingMobileList,
    renderedPOIs: state.map.renderedPOIs,
    searchResultsSummary: state.exploreService.searchResultsSummary,
    staticStyleIds: state.map.staticStyleIds,
    trailEngagement: state.exploreService.trailEngagement
  }));

  const onClick = useCallback(() => {
    logExploreViewSwitched({
      bearing: map?.getBearing(),
      coordinates: map ? [map?.getCenter().lng, map?.getCenter().lat] : undefined,
      explore_view_type: exploreViewType ?? 'map',
      filters_applied: getFilterNames(exploreServiceFilters, Boolean(distanceAwayIsochrone), Boolean(trailEngagement)),
      map_base_layer: getMapBaseLayer(baseStyleId),
      map_overlays: [...dynamicStyleIds, ...staticStyleIds].map(id => getMapOverlay(id) as MapOverlay),
      method: 'click',
      new_state: isShowingMobileList ? 'map' : 'list',
      num_pois: renderedPOIs?.count,
      num_results_returned: searchResultsSummary?.count,
      pitch: map?.getPitch(),
      previous_state: isShowingMobileList ? 'list' : 'map',
      zoom_level: map?.getZoom()
    });
    dispatch(setIsShowingMobileList(!isShowingMobileList));
  }, [
    baseStyleId,
    dispatch,
    distanceAwayIsochrone,
    dynamicStyleIds,
    exploreServiceFilters,
    exploreViewType,
    isShowingMobileList,
    map,
    renderedPOIs,
    searchResultsSummary?.count,
    staticStyleIds,
    trailEngagement
  ]);

  return (
    <Button
      className={classNames(styles.mobileViewToggle, className)}
      icon={{ Component: isShowingMobileList ? MapFilled : List }}
      onClick={onClick}
      text={isShowingMobileList ? <FormattedMessage defaultMessage="Map" /> : isExploreTrail ? <FormattedMessage defaultMessage="Trail" /> : <FormattedMessage defaultMessage="List" />}
      testId="explore-view-toggle"
      variant="accent"
    />
  );
};

export default MobileViewToggle;
